import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MobileFrame } from './components/ui/MobileFrame';
import { BottomTabBar } from './components/ui/BottomTabBar';
import { ThemeProvider } from './contexts/ThemeContext';
import { IncomingCallBanner } from './components/ui/IncomingCallBanner';
import { EmergencyCallNotification } from './components/ui/EmergencyCallNotification';
import { VexuNotification } from './components/ui/VexuNotification';
// New Onboarding Screens
import { WelcomeScreen } from './pages/WelcomeScreen';
import { FeatureCarouselScreen } from './pages/FeatureCarouselScreen';
import { PhoneEntryScreen } from './pages/PhoneEntryScreen';
import { OTPScreen } from './pages/OTPScreen';
import { NameEntryScreen } from './pages/NameEntryScreen';
import { BackupCheckScreen } from './pages/BackupCheckScreen';
// Main App Screens
import { HomeScreen } from './pages/HomeScreen';
import { CalendarScreen } from './pages/CalendarScreen';
import { CallsScreen } from './pages/CallsScreen';
import { CallDetailScreen } from './pages/CallDetailScreen';
import { ActiveCallScreen } from './pages/ActiveCallScreen';
import { UsageScreen } from './pages/UsageScreen';
import { ProfileScreen } from './pages/ProfileScreen';
import { SettingsScreen } from './pages/SettingsScreen';
import { CallerExperienceScreen } from './pages/CallerExperienceScreen';
import { PlanSelectionScreen } from './pages/PlanSelectionScreen';
import { SubscriptionConsentScreen } from './pages/SubscriptionConsentScreen';
import { ActivationGuideScreen } from './pages/ActivationGuideScreen';
import { SupportChatScreen } from './pages/SupportChatScreen';
import { NotificationsScreen } from './pages/NotificationsScreen';
import { ProCheckoutScreen } from './pages/ProCheckoutScreen';
import { ProActivationSuccessScreen } from './pages/ProActivationSuccessScreen';
import { IosActivationFlow } from './pages/IosActivationFlow';
import { IosDeactivationFlow } from './pages/IosDeactivationFlow';
import { ForwardingSetupScreen } from './pages/ForwardingSetupScreen';
import { DEFAULT_SUBSCRIPTION_PLAN_ID, getSubscriptionPlan, SubscriptionPlanId } from './data/subscriptionPlans';
import { LandlineDeactivationScreen } from './pages/LandlineDeactivationScreen';
import { OnboardingTour, TourStep } from './components/ui/OnboardingTour';
import { useScreenInit } from './useScreenInit.js';
import { PresentationRoot } from './components/presentation/PresentationRoot';

/** Routes that mount the Vexu mobile app directly (used by Screens previews). */
const APP_ONLY_ROUTES = ['/forwardingSetup', '/iosDeactivation', '/planSelection', '/subscriptionConsent', '/proCheckout'];

/** Root: the Vexu AI × Zain executive presentation. */
export function App() {
  const isAppRoute = typeof window !== 'undefined' && APP_ONLY_ROUTES.includes(window.location.pathname);
  if (isAppRoute) return <VexuMobileApp data-id="element-0" data-mp="2swlix0t2rlrf" />;
  return <PresentationRoot renderVexuApp={() => <VexuMobileApp frameBackdropClassName="bg-transparent text-slate-900" data-id="element-2" data-mp="18d9eyc0rhuut" />} data-id="element-1" data-mp="2svadyxnw87yq" />;
}
const HOME_TOUR_STEPS: TourStep[] = [{
  id: 'welcome',
  title: 'Welcome to Vexu',
  description: "Your AI assistant answers calls when you can't. Here's a quick tour of your home base.",
  spotlightSelector: '[data-tour="home-header"]',
  prefer: 'below',
  highlightPadding: 12
}, {
  id: 'forwarding',
  title: 'Call forwarding',
  description: 'Forward your calls to Vexu so the AI can pick up whenever you’re busy, away, or unreachable.',
  spotlightSelector: '[data-tour="call-forwarding"]',
  prefer: 'below',
  highlightPadding: 10
}, {
  id: 'activate',
  title: 'Activate Vexu Pro',
  description: 'Start your 3-day free trial to unlock 24/7 AI call handling, smart scheduling, and more.',
  spotlightSelector: '[data-tour="activate-button"]',
  prefer: 'above',
  highlightPadding: 10
}, {
  id: 'tabs',
  title: 'Find your way around',
  description: 'Jump between Home, Calls, Calendar, and your Account anytime from the bar down here.',
  spotlightSelector: '[data-tour="bottom-tabs"]',
  prefer: 'above',
  highlightPadding: 8
}];
const CALLS_TOUR_STEPS: TourStep[] = [{
  id: 'calls-list',
  title: 'Every call, handled',
  description: 'See your AI-handled calls here. Live calls show a green dot — tap any call to read the full transcript and summary.',
  spotlightSelector: '[data-tour="calls-list-item"]',
  prefer: 'below',
  highlightPadding: 8
}, {
  id: 'calls-search',
  title: 'Find any call fast',
  description: 'Search by contact name or group to jump straight to the conversation you need.',
  spotlightSelector: '[data-tour="calls-search"]',
  prefer: 'below',
  highlightPadding: 8
}, {
  id: 'calls-caller-experience',
  title: 'Tune the experience',
  description: 'Customize how Vexu greets and responds to different callers from Caller Experience.',
  spotlightSelector: '[data-tour="calls-caller-experience"]',
  prefer: 'below',
  highlightPadding: 8
}];
interface VexuMobileAppProps {
  /** Overrides the surface behind the device frame when embedded elsewhere. */
  frameBackdropClassName?: string;
}
export function VexuMobileApp({
  frameBackdropClassName
}: VexuMobileAppProps = {}) {
  const screenInit = useScreenInit();
  const routeScreenMap: Record<string, string> = {
    '/forwardingSetup': 'forwardingSetup',
    '/iosDeactivation': 'iosDeactivation',
    '/planSelection': 'planSelection',
    '/subscriptionConsent': 'subscriptionConsent',
    '/proCheckout': 'proCheckout'
  };
  const initialScreen = typeof window !== 'undefined' ? routeScreenMap[window.location.pathname] ?? 'welcome' : 'welcome';
  const initialPlanId: SubscriptionPlanId = screenInit.selectedPlanId === 'growth' || screenInit.selectedPlanId === 'scale' ? screenInit.selectedPlanId : DEFAULT_SUBSCRIPTION_PLAN_ID;
  // State for navigation and onboarding
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showCallsTour, setShowCallsTour] = useState(false);
  const [callsTourSeen, setCallsTourSeen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  // Store phone number and selected OTP delivery method during onboarding
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationMethod, setVerificationMethod] = useState<'whatsapp' | 'call'>('whatsapp');
  const [callDetailOpen, setCallDetailOpen] = useState(false);
  const [callToOpenId, setCallToOpenId] = useState<number | null>(null);
  const [calendarEventToOpenId, setCalendarEventToOpenId] = useState<number | null>(null);
  // Activation state (set after plan selection)
  const [isActivated, setIsActivated] = useState(false);
  // AI answering rules enabled during the iOS activation flow.
  // These drive which scenarios show as active on the Home dashboard.
  const [enabledRules, setEnabledRules] = useState<string[]>([]);
  // The number type selected immediately after subscription determines the
  // appropriate forwarding and deactivation instructions.
  const [forwardingNumberType, setForwardingNumberType] = useState<'mobile' | 'landline' | null>(screenInit.forwardingNumberType === 'landline' ? 'landline' : null);
  // The selector is shared by post-checkout setup and Home. Keep its return
  // destination explicit so Home re-entry never leads back to checkout.
  const [forwardingSetupOrigin, setForwardingSetupOrigin] = useState<'checkout' | 'home'>('checkout');
  // The concrete answering rules that "Answer All Calls" ('all') expands into.
  // When 'all' is active and the user deactivates a single rule, we first
  // expand 'all' to these ids so the other three stay active.
  const ALL_RULE_IDS = ['busy', 'unanswered', 'unreachable', 'off'];
  const expandAll = (rules: string[]) => rules.includes('all') ? ALL_RULE_IDS : rules;
  // When the user activates an ADDITIONAL rule from the Home card, we reuse the
  // same activation flow seeded with just this condition (subscription already active).
  const [addRuleTarget, setAddRuleTarget] = useState<string | null>(null);
  // When the user deactivates a rule from the Home card, we use the deactivation flow.
  const [deactivateTarget, setDeactivateTarget] = useState<string | null>(null);
  // Indicates that a carrier deactivation was started as part of cancelling the subscription.
  const [cancelAfterForwardingDeactivation, setCancelAfterForwardingDeactivation] = useState(false);
  // Show the first-run welcome banner on Caller Experience (set when the user
  // taps "Customize My AI" right after activation).
  const [callerExpWelcome, setCallerExpWelcome] = useState(false);
  // Billing state persists across the staged plan → consent → payment journey.
  const [selectedPlanId, setSelectedPlanId] = useState<SubscriptionPlanId>(initialPlanId);
  const selectedPlan = getSubscriptionPlan(selectedPlanId);
  // The promotional trial can be enabled or withdrawn without changing the plan itself.
  const [hasTrialOffer, setHasTrialOffer] = useState(screenInit.hasTrialOffer !== false);
  // Vexu Promote: opt-in for 20% bonus minutes, managed from checkout and Usage.
  const [promoEnabled, setPromoEnabled] = useState(false);
  // A promo code is entered while choosing a plan, before the consent and payment steps.
  const [promoCode, setPromoCode] = useState('');
  // Trial state — active but within the 3-day free trial (no charge yet)
  const [isTrial, setIsTrial] = useState(false);
  // Usage simulation state (driven by external controls)
  const [simOverage, setSimOverage] = useState(false);
  const handleSimulateFree = () => {
    setIsActivated(false);
    setIsTrial(false);
    setSimOverage(false);
    handleNavigate('usage');
  };
  const handleSimulateTrial = () => {
    setIsActivated(true);
    setIsTrial(true);
    setSimOverage(false);
    handleNavigate('usage');
  };
  const handleSimulateActive = () => {
    setIsActivated(true);
    setIsTrial(false);
    setSimOverage(false);
    handleNavigate('usage');
  };
  const handleSimulateOverage = () => {
    setIsActivated(true);
    setIsTrial(false);
    setSimOverage(true);
    handleNavigate('usage');
  };
  const completeSubscriptionCancellation = () => {
    setIsActivated(false);
    setIsTrial(false);
    setEnabledRules([]);
    setVexuStatus(null);
    setDeactivateTarget(null);
    setCancelAfterForwardingDeactivation(false);
    handleNavigate('home');
  };
  const previewConsentOffer = (hasTrial: boolean) => {
    setSelectedPlanId(DEFAULT_SUBSCRIPTION_PLAN_ID);
    setHasTrialOffer(hasTrial);
    setIsActivated(false);
    setIsTrial(false);
    setSimOverage(false);
    handleNavigate('subscriptionConsent');
  };
  // Incoming call simulation state
  const [incomingCall, setIncomingCall] = useState<any>(null);
  // Emergency demo: OS notification card + the timers that advance the demo.
  const [showEmergencyNotification, setShowEmergencyNotification] = useState(false);
  const emergencyTimersRef = useRef<number[]>([]);
  useEffect(() => () => {
    emergencyTimersRef.current.forEach(id => window.clearTimeout(id));
  }, []);
  // Persistent Vexu system notification (sticky while call forwarding is active)
  const [vexuStatus, setVexuStatus] = useState<'active' | 'busy' | 'dnd' | null>(null);
  const handleToggleVexuNotification = () => {
    setVexuStatus(prev => prev === null ? 'active' : prev === 'active' ? 'busy' : prev === 'busy' ? 'dnd' : null);
  };
  // Navigation handlers
  const handleGetStarted = () => setCurrentScreen('features');
  const handleExplore = () => {
    setOnboardingComplete(true);
    setCurrentScreen('home');
    setShowTour(true);
  };
  const handleFeaturesComplete = () => setCurrentScreen('phoneEntry');
  const handlePhoneSubmit = (phone: string, deliveryMethod: 'whatsapp' | 'call') => {
    setPhoneNumber(phone);
    setVerificationMethod(deliveryMethod);
    setCurrentScreen('otp');
  };
  const handleOtpVerify = () => {
    setCurrentScreen('backupCheck');
  };
  const handleNameComplete = (name: string) => {
    setOnboardingComplete(true);
    setCurrentScreen('home');
    setShowTour(true);
  };
  // Start the Calls tour the first time the user lands on the Calls list
  const maybeStartCallsTour = (screen: string) => {
    if (screen === 'calls' && !callsTourSeen && !showTour) {
      setCallsTourSeen(true);
      setShowCallsTour(true);
    }
  };
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentScreen(tab);
    maybeStartCallsTour(tab);
  };
  const handleNavigate = (screen: string) => {
    setCurrentScreen(screen);
    if (['home', 'calendar', 'calls', 'usage', 'profile'].includes(screen)) {
      setActiveTab(screen);
    }
    maybeStartCallsTour(screen);
  };
  const [showOnboardingWarning, setShowOnboardingWarning] = useState(false);
  const handleSimulateCall = () => {
    if (!onboardingComplete) {
      setShowOnboardingWarning(true);
      setTimeout(() => setShowOnboardingWarning(false), 4000);
      return;
    }
    const callers = [{
      name: 'Alex Johnson',
      initials: 'AJ',
      colorLight: 'bg-blue-100 text-blue-700',
      colorDark: 'bg-blue-100/60 text-blue-600',
      group: 'Work Contacts'
    }, {
      name: 'Emily Chen',
      initials: 'EC',
      colorLight: 'bg-emerald-100 text-emerald-700',
      colorDark: 'bg-emerald-100/60 text-emerald-600',
      group: 'VIP Clients'
    }, {
      name: 'David Park',
      initials: 'DP',
      colorLight: 'bg-purple-100 text-purple-700',
      colorDark: 'bg-purple-100/60 text-purple-600',
      group: 'Personal'
    }];
    const randomCaller = callers[Math.floor(Math.random() * callers.length)];
    setIncomingCall(randomCaller);
    // Auto clear after 6 seconds only if NOT on calls screen (calls screen keeps it persistent)
    if (currentScreen !== 'calls') {
      setTimeout(() => {
        setIncomingCall((prev: any) => prev?.name === randomCaller.name ? null : prev);
      }, 6000);
    }
  };
  // One guided demo that walks the emergency call through all three states:
  // notification bar → active call → completed entry in the call log.
  const handleSimulateEmergencyCall = () => {
    if (!onboardingComplete) {
      setShowOnboardingWarning(true);
      setTimeout(() => setShowOnboardingWarning(false), 4000);
      return;
    }
    emergencyTimersRef.current.forEach(id => window.clearTimeout(id));
    emergencyTimersRef.current = [];
    setCallToOpenId(null);
    // Don't let the first-run Calls tour interrupt the emergency demo.
    setCallsTourSeen(true);
    setShowCallsTour(false);
    setIncomingCall({
      name: 'Michael Torres',
      initials: 'MT',
      colorLight: 'bg-red-100 text-red-700',
      colorDark: 'bg-red-500/20 text-red-400',
      group: 'Emergency',
      isEmergency: true
    });
    // 1. Land on Home so the system notification is what the user sees first.
    handleNavigate('home');
    setShowEmergencyNotification(true);
    // 2. Open the live call so the active-call treatment plays out.
    emergencyTimersRef.current.push(window.setTimeout(() => {
      setShowEmergencyNotification(false);
      handleNavigate('calls');
      setCallToOpenId(999);
    }, 3600));
  };
  const openEmergencyCall = () => {
    emergencyTimersRef.current.forEach(id => window.clearTimeout(id));
    emergencyTimersRef.current = [];
    setShowEmergencyNotification(false);
    handleNavigate('calls');
    setCallToOpenId(999);
  };
  // Render current screen content
  const renderScreen = () => {
    switch (currentScreen) {
      // New Onboarding Flow
      case 'welcome':
        return <WelcomeScreen onGetStarted={handleGetStarted} onExplore={handleExplore} data-id="element-3" data-mp="ta0dk2bh9d8n" />;
      case 'features':
        return <FeatureCarouselScreen onContinue={handleFeaturesComplete} data-id="element-4" data-mp="jh2r5l3d744r" />;
      case 'phoneEntry':
        return <PhoneEntryScreen onBack={() => setCurrentScreen('features')} onContinue={handlePhoneSubmit} data-id="element-5" data-mp="ofw4ndiniv4x" />;
      case 'otp':
        return <OTPScreen phoneNumber={phoneNumber} initialDeliveryMethod={verificationMethod} onBack={() => setCurrentScreen('phoneEntry')} onVerify={handleOtpVerify} data-id="element-6" data-mp="fhk68pbjmt1x" />;
      case 'nameEntry':
        return <NameEntryScreen onBack={() => setCurrentScreen('backupCheck')} onContinue={handleNameComplete} data-id="element-7" data-mp="1uxizkugi4ki4" />;
      case 'backupCheck':
        return <BackupCheckScreen onBack={() => setCurrentScreen('otp')} onContinue={() => setCurrentScreen('nameEntry')} data-id="element-8" data-mp="3ke53hstty5gt" />;
      // Main Tabs
      case 'home':
        return <HomeScreen onNavigate={handleNavigate} isActivated={isActivated} enabledRules={enabledRules} plan={selectedPlan} onChooseForwardingNumber={() => {
          setForwardingSetupOrigin('home');
          handleNavigate('forwardingSetup');
        }} onAddRule={conditionId => {
          setAddRuleTarget(conditionId);
          handleNavigate('iosActivation');
        }} onDeactivateRule={conditionId => {
          setDeactivateTarget(conditionId);
          handleNavigate('iosDeactivation');
        }} data-id="element-9" data-mp="3u4hkxc6cliiq" />;
      case 'calendar':
        return <CalendarScreen onViewCall={callId => {
          setCallToOpenId(callId);
          handleNavigate('calls');
        }} initialEventId={calendarEventToOpenId} onInitialEventOpened={() => setCalendarEventToOpenId(null)} data-id="element-10" data-mp="2dq7xck9xjxsy" />;
      case 'calls':
        return <CallsScreen onNavigate={handleNavigate} onDetailView={setCallDetailOpen} simulatedCall={incomingCall} initialCallId={callToOpenId} onInitialCallOpened={() => setCallToOpenId(null)} onViewBookedEvent={eventId => {
          setCalendarEventToOpenId(eventId);
          handleNavigate('calendar');
        }} data-id="element-11" data-mp="1wukh7mugdwv6" />;
      case 'usage':
        return <UsageScreen onNavigate={handleNavigate} isActivated={isActivated} isTrial={isTrial} simOverage={simOverage} promoEnabled={promoEnabled} plan={selectedPlan} onPromoChange={setPromoEnabled} onForwardingDeactivationRequested={numberType => {
          setForwardingNumberType(numberType);
          setDeactivateTarget('all');
          setCancelAfterForwardingDeactivation(true);
          handleNavigate('iosDeactivation');
        }} onCancelSubscription={completeSubscriptionCancellation} data-id="element-12" data-mp="1dz3vgvhrjt9s" />;
      case 'profile':
        return <ProfileScreen onNavigate={handleNavigate} isActivated={isActivated} plan={selectedPlan} onLogout={() => {
          setOnboardingComplete(false);
          setIsActivated(false);
          setIsTrial(false);
          setCurrentScreen('welcome');
        }} data-id="element-13" data-mp="dvcvngvh2w0o" />;
      // Subscription flow: plan → required consent → card payment → forwarding.
      case 'planSelection':
        return <PlanSelectionScreen onBack={() => handleNavigate('profile')} promoCode={promoCode} onPromoCodeChange={setPromoCode} onSelectPlan={planId => {
          setSelectedPlanId(planId);
          setHasTrialOffer(true);
          handleNavigate('subscriptionConsent');
        }} data-id="element-14" data-mp="3m5xy7zrrdiiv" />;
      case 'subscriptionConsent':
        return <SubscriptionConsentScreen plan={selectedPlan} hasTrialOffer={hasTrialOffer} onBack={() => handleNavigate('planSelection')} onContinue={() => handleNavigate('proCheckout')} data-id="element-15" data-mp="27919276q65uc" />;
      case 'proCheckout':
        return <ProCheckoutScreen plan={selectedPlan} hasTrialOffer={hasTrialOffer} onBack={() => handleNavigate('subscriptionConsent')} promoCode={promoCode} promoEnabled={promoEnabled} onPromoChange={setPromoEnabled} onSuccess={() => {
          setIsActivated(true);
          setIsTrial(hasTrialOffer);
          setForwardingSetupOrigin('checkout');
          handleNavigate('forwardingSetup');
        }} data-id="element-16" data-mp="1ti5xgn4udan2" />;
      case 'forwardingSetup':
        return <ForwardingSetupScreen onBack={() => handleNavigate(forwardingSetupOrigin === 'home' ? 'home' : 'proCheckout')} onChooseMobile={() => {
          setForwardingNumberType('mobile');
          handleNavigate('iosActivation');
        }} onLandlineActivated={() => {
          setForwardingNumberType('landline');
          setEnabledRules(['all']);
          handleNavigate('proActivationSuccess');
        }} data-id="element-17" data-mp="3hdkka8l9wyt6" />;
      case 'proActivationSuccess':
        return <ProActivationSuccessScreen plan={selectedPlan} onComplete={() => handleNavigate('home')} data-id="element-18" data-mp="37fo5n6t4nlqb" />;
      case 'iosActivation':
        return <IosActivationFlow addRuleCondition={addRuleTarget as any} subscriptionActive={forwardingNumberType === 'mobile' && !addRuleTarget} onSubscriptionSuccess={() => {
          setIsActivated(true);
          setIsTrial(true);
          handleNavigate('forwardingSetup');
        }} onComplete={(conditions, destination) => {
          if (addRuleTarget) {
            // Adding a single rule to an already-active subscription:
            // merge the new condition into the existing enabled rules.
            setEnabledRules(prev => Array.from(new Set([...prev, ...conditions])));
            setAddRuleTarget(null);
          } else {
            setIsActivated(true);
            setIsTrial(true);
            setEnabledRules(conditions);
          }
          if (destination === 'callerExperience') {
            setCallerExpWelcome(true);
            handleNavigate('callerExperience');
          } else {
            handleNavigate('home');
          }
        }} onCancel={() => {
          if (addRuleTarget) {
            // Cancelling an add-rule flow must NOT cancel the subscription.
            setAddRuleTarget(null);
          } else {
            setIsActivated(false);
            setIsTrial(false);
            setEnabledRules([]);
          }
          handleNavigate('home');
        }} onBack={() => {
          setAddRuleTarget(null);
          handleNavigate('home');
        }} onContactSupport={() => handleNavigate('support')} data-id="element-19" data-mp="mpfbzly2lrnt" />;
      case 'iosDeactivation':
        if (forwardingNumberType === 'landline') {
          return <LandlineDeactivationScreen onBack={() => {
            setDeactivateTarget(null);
            setCancelAfterForwardingDeactivation(false);
            handleNavigate('usage');
          }} onComplete={() => {
            if (cancelAfterForwardingDeactivation) {
              completeSubscriptionCancellation();
              return;
            }
            setEnabledRules([]);
            setDeactivateTarget(null);
            setVexuStatus(null);
            handleNavigate('home');
          }} data-id="element-20" data-mp="1kzu4vf5saj74" />;
        }
        return <IosDeactivationFlow conditionId={deactivateTarget as any} isLastActiveRule={
        // Deactivating the "All Calls" mode turns everything off.
        deactivateTarget === 'all' || expandAll(enabledRules).filter(c => c !== deactivateTarget).length === 0} onComplete={deactivatedCondition => {
          setEnabledRules(prev => deactivatedCondition === 'all' ? [] : expandAll(prev).filter(c => c !== deactivatedCondition));
          setDeactivateTarget(null);
          handleNavigate('home');
        }} onEnableAnotherRule={() => {
          setDeactivateTarget(null);
          setCancelAfterForwardingDeactivation(false);
          handleNavigate('home');
          // Note: In a real app, this might open the add rule flow directly,
          // but returning to home where they can tap Activate is fine.
        }} onCancelSubscription={completeSubscriptionCancellation} onKeepSubscription={() => {
          setEnabledRules(prev => deactivateTarget === 'all' ? [] : expandAll(prev).filter(c => c !== deactivateTarget));
          setDeactivateTarget(null);
          setCancelAfterForwardingDeactivation(false);
          handleNavigate('home');
        }} finishAfterDeactivation={cancelAfterForwardingDeactivation} onBack={() => {
          setDeactivateTarget(null);
          setCancelAfterForwardingDeactivation(false);
          handleNavigate('usage');
        }} data-id="element-21" data-mp="b3ysg12cnymt" />;
      case 'activationGuide':
        return <ActivationGuideScreen onComplete={() => handleNavigate('home')} data-id="element-22" data-mp="1voq9rbcs8tnw" />;
      case 'callDetail':
        return <CallDetailScreen onBack={() => handleNavigate('calls')} data-id="element-23" data-mp="29ku0uvsd0lw5" />;
      case 'settings':
        return <SettingsScreen onBack={() => handleNavigate('profile')} data-id="element-24" data-mp="24py63nvqnkfc" />;
      case 'support':
        return <SupportChatScreen onBack={() => handleNavigate('profile')} data-id="element-25" data-mp="2jj9oupw3bf1f" />;
      case 'notifications':
        return <NotificationsScreen onBack={() => handleNavigate('profile')} data-id="element-26" data-mp="2ejx4qcwlsm2j" />;
      case 'callerExperience':
        return <CallerExperienceScreen onBack={() => {
          setCallerExpWelcome(false);
          handleNavigate('home');
        }} isActivated={isActivated} onNavigate={handleNavigate} showWelcome={callerExpWelcome} onDismissWelcome={() => setCallerExpWelcome(false)} data-id="element-27" data-mp="2obt7w2djr2dm" />;
      case 'activeCall':
        // Hidden screen for demo purposes
        return <ActiveCallScreen onEndCall={() => handleNavigate('home')} data-id="element-28" data-mp="3lwclgwnsah99" />;
      default:
        return <HomeScreen onNavigate={handleNavigate} data-id="element-29" data-mp="370wke7osdl3d" />;
    }
  };
  // Check if we should show the bottom tab bar
  const showTabBar = onboardingComplete && ['home', 'calendar', 'calls', 'profile'].includes(currentScreen) && !callDetailOpen;
  return <ThemeProvider data-id="element-30" data-mp="2y5yoqsu97u8c">
      <MobileFrame backdropClassName={frameBackdropClassName} onSimulateCall={handleSimulateCall} onSimulateEmergencyCall={handleSimulateEmergencyCall} onSimulateFree={handleSimulateFree} onSimulateTrial={handleSimulateTrial} onSimulateConsentWithTrial={() => previewConsentOffer(true)} onSimulateConsentWithoutTrial={() => previewConsentOffer(false)} onSimulateActive={handleSimulateActive} onSimulateOverage={handleSimulateOverage} onToggleVexuNotification={handleToggleVexuNotification} vexuNotificationActive={vexuStatus !== null} vexuNotificationStatus={vexuStatus === 'active' ? 'Active' : vexuStatus === 'busy' ? 'Busy' : vexuStatus === 'dnd' ? 'DND' : 'Off'} data-id="element-31" data-mp="32y9ptz0qdm2h">
        <div className="h-full flex flex-col relative" data-id="element-32" data-mp="2kv58ut75om6d">
          <AnimatePresence data-id="element-33" data-mp="2gs8u5dkqla7p">
            {vexuStatus && <VexuNotification key="vexu-notification" status={vexuStatus} onTap={() => handleNavigate('home')} data-id="element-34" data-mp="2c0h1mxi0suex" />}
            {showEmergencyNotification && incomingCall?.isEmergency && <EmergencyCallNotification key="emergency-notification" callerName={incomingCall.name} onTap={openEmergencyCall} data-id="element-35" data-mp="2244aa69y6snc" />}
            {incomingCall && !incomingCall.isEmergency && ['home', 'calendar', 'profile'].includes(currentScreen) && <IncomingCallBanner key="incoming-banner" call={incomingCall} onTap={() => handleNavigate('calls')} onDismiss={() => setIncomingCall(null)} data-id="element-36" data-mp="cke8es9qz86f" />}
            {showOnboardingWarning && <motion.div key="onboarding-warning" initial={{
            opacity: 0,
            y: -60,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -40,
            scale: 0.95
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 28
          }} className="absolute top-4 left-3 right-3 z-[60]" data-id="element-37" data-mp="3l1qtw1kab89y">
                <div className="bg-white rounded-2xl p-4 shadow-xl border border-slate-200 flex items-center gap-3" data-id="element-38" data-mp="2lz9wcze2j8kd">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-39" data-mp="27xxfmlr332xf">
                    <span className="text-lg" data-id="element-40" data-mp="2byzg15wqll0z">👋</span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-41" data-mp="1y84gwlc1iial">
                    <p className="text-sm font-bold text-slate-900" data-id="element-42" data-mp="1y7ci71n5c4g3">
                      Complete setup first
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-43" data-mp="1obrbiq74ug00">
                      Login or explore the app, then try simulating a call.
                    </p>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          <div className="flex-1 overflow-hidden relative" data-id="element-44" data-mp="1k9efwum3nygy">
            {renderScreen()}
          </div>

          {showTabBar && <BottomTabBar currentTab={activeTab} onTabChange={handleTabChange} data-id="element-45" data-mp="10onp5kk9qfo0" />}

          <OnboardingTour isActive={showTour || showCallsTour} steps={showTour ? HOME_TOUR_STEPS : CALLS_TOUR_STEPS} onComplete={() => {
          setShowTour(false);
          setShowCallsTour(false);
        }} onSkip={() => {
          setShowTour(false);
          setShowCallsTour(false);
        }} data-id="element-46" data-mp="vqcjzn4x4tc4" />
        </div>
      </MobileFrame>
    </ThemeProvider>;
}